import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import SurveySummary from "../components/survey_overview/SurveySummary";
import SummaryTop5s from "./SummaryTop5s";
import survey_diag_api from "../api/protected/diagnostics";
import SummaryPillars from "./SummaryPillars";
import SummaryQuestions from "./SummaryQuestions";
import reports_api from "../api/protected/reports";
import { captureElementAsCanvas, waitForLayout } from "../utils/captureForPdf";
import profile_api from "../api/protected/profile";
import OverviewReportConfigurator from "../components/OverviewReportConfigurator";


const SurveyOverview = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState(null);

  const [showSummary, setShowSummary] = useState(true);
  const [showTop5s, setShowTop5s] = useState(false);
  const [showPillars, setShowPillars] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);

  const [surveyData, setSurveyData] = useState();
  const [cardId, setCardId] = useState(0);

  const [comparisonSurveys, setComparisonSurveys] = useState([]);
  const [demo, setDemo] = useState(false);

  const [showReportGenerated, setShowReportGenerated] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showReportCover, setShowReportCover] = useState(false);
  const [showConfigurator, setShowConfigurator] = useState(false);
  const [reportConfig, setReportConfig] = useState(null);

  const [showDetailCard, setShowDetailCard] = useState(true);
  const [showGraphicalChart, setShowGraphicalChart] = useState(true);
  const [showCollectiveScores, setShowCollectiveScores] = useState(true);
  const [showGraphicalData, setShowGraphicalData] = useState(true);
  const [showTabularData, setShowTabularData] = useState(true);
  const [showCoachingProgramme, setShowCoachingProgramme] = useState(true);
  const [showCoachingExercises, setShowCoachingExercises] = useState(true);
  const [showPillarTables, setShowPillarTables] = useState(true);
  const [showReportError, setShowReportError] = useState(false);

  // origin-aware Questions support
  const [questionsOrigin, setQuestionsOrigin] = useState(null);
  const [questionsData, setQuestionsData] = useState(null);
  const [questionsMaps, setQuestionsMaps] = useState(null); // { chartMap: Theme[][], top5Map: {0..3: Theme[]} }
  
  useEffect(() => {
    // console.log("[SurveyOverview] questionsMaps updated:", questionsMaps);
  }, [questionsMaps]);

  const [hasStakeholders, setHasStakeholders] = useState(false);

  const getSubscription = () => {
    let response = profile_api.getSubscription();
    response.then(response => {
      setSubscription(response.data.subscription.type);
    }).catch(() => {});
  };

  useEffect(() => {
    getSubscription();
  }, []);

  useEffect(() => {
    if (subscription !== null && subscription < 1) {
      setShowPillarTables(false);
    }
  }, [subscription]);

  const getSurvey = () => {
    let participants = [];
    if (searchParams.get("selected")) {
      let s = searchParams.get("selected").trim();
      participants = s.substring(1, s.length - 1).split(",");
    } else {
      navigate("/surveys/" + params.surveyId + "?overview=true");
    }

    const body = {};
    if (participants.length) body.participants = participants;
    else return;

    let response;
    if (searchParams.get("demo")) {
      response = survey_diag_api.getDemoSurveyDiagnostics(params.surveyId, body);
    } else {
      response = survey_diag_api.getSurveyDiagnostics(params.surveyId, body);
    }

    response
      .then((res) => {
        const data = { ...res.data };
        if (data.Error) return;
        setSurveyData(data);
        setHasStakeholders((data.participants?.stakeholders?.length ?? 0) > 0);
      })
      .catch((err) => console.log(err));
  };

  const getComparisonSurveys = () => {
    if (searchParams.get("demo")) {
      const resp = survey_diag_api.getDiagnosticComparisons(
        params.surveyId,
        searchParams.get("demo")
      );
      setDemo(true);
      resp
        .then((r) => setComparisonSurveys(r.data.surveys))
        .catch((err) => console.log(err));
    } else {
      const resp = survey_diag_api.getDiagnosticComparisons(params.surveyId, demo);
      resp
        .then((r) => setComparisonSurveys(r.data.surveys))
        .catch((err) => console.log(err));
    }
  };

  const getTeamVariance = () => {
    if (!surveyData) return [];
    return surveyData.pillars.map((pillar) =>
      pillar.themes.map((theme) => {
        return theme.team_variance
      })
    );
  };

  const getTeamMean = () => {
    if (!surveyData) return [];
    return surveyData.pillars.map((pillar) =>
        pillar.themes.map((theme) => {
          return theme.team_avg_score
        })
    );
  };

  const scrollTop = () => window.scrollTo(0, 0);

  const updateShowSummary = () => {
    setShowTop5s(false);
    setShowPillars(false);
    setShowQuestions(false);
    setShowSummary(true);
    scrollTop();
  };

  const updateShowTop5s = (id) => {
    setShowSummary(false);
    setShowQuestions(false);
    setShowPillars(false);
    setShowTop5s(true);
    setCardId(id);
    scrollTop();
  };

  const updateShowPillars = (pillar_id) => {
    setShowSummary(false);
    setShowQuestions(false);
    setShowTop5s(false);
    setShowPillars(true);
    setCardId(pillar_id);
    scrollTop();
  };

  const updateShowQuestions = (pillar_id, origin) => {
    setShowSummary(false);
    setShowTop5s(false);
    setShowPillars(false);
    setShowQuestions(true);
    setCardId(pillar_id);
    setQuestionsOrigin(origin);

    // pick precomputed payload from SurveySummary if available
    if (questionsMaps) {
      const payload =
        origin === "chart"
          ? questionsMaps.chartMap?.[pillar_id] ?? null
          : questionsMaps.top5Map?.[pillar_id] ?? null; // 0..3 for Top5s groups
      setQuestionsData(payload);
    } else {
      setQuestionsData(null);
    }
    scrollTop();
  };

  const backFromQuestions = (pillar_id, origin) => {
    // leave questions view
    setShowQuestions(false);

    // restore the correct tabular context + selected card
    if (origin === "top5s") {
      setShowTop5s(true);
      setShowPillars(false);
    } else {
      setShowTop5s(false);
      setShowPillars(true);
    }
    setCardId(pillar_id);
    scrollTop();
  };


  const switchCard = (id) => setCardId(id);

  useEffect(() => {
    if (!params.surveyId || generatingReport) return;
    getSurvey();
    getComparisonSurveys();
  }, [params.surveyId]);

  const OpenConfigurator = (ev) => {
    if (!ev) return;
    setShowConfigurator(true);
  };

  const CloseConfigurator = (ev) => {
    if (!ev) return;
    setShowConfigurator(false);
  };

  const HandleConfiguratorGenerate = () => {
    setShowConfigurator(false);
    setShowReportCover(true);
  };

  useEffect(() => {
    if (!showReportCover) return;
    setGeneratingReport(true);
  }, [showReportCover]);

  const finaliseReport = (img_data) => {
    if (!surveyData) return;

    const report_details = {
      client_name: surveyData.organisation_name,
      team_name: surveyData.team_name,
      comparing_surveys: "N/A",
      tm_count: surveyData.participants.team_members.length,
      tl_count: surveyData.participants.team_leaders.length,
      st_count: surveyData.participants.stakeholders.length,
      survey_id: surveyData.survey_id,
      team_id: surveyData.team_id,
      demo: !!searchParams.get("demo"),
    };

    reports_api
        .finaliseReport("summary", report_details, surveyData, reportConfig, img_data)
        .then(() => {
          setShowReportGenerated(true);
          setGeneratingReport(false);
          setShowReportCover(false);
        })
        .catch((err) => {
          console.log("ERROR in finaliseReport:", err);
          setGeneratingReport(false);
          setShowReportCover(false);
          setShowReportError(true);
        });
  };


  // -------- PDF Questions paging (from 654) ----------
  const QUESTIONS_MAX_ACROSS = 10;
  const teamVarianceByPillar = useMemo(() => getTeamVariance(), [surveyData]);
  const teamMeanByPillar = useMemo(() => getTeamMean(), [surveyData]);

  const questionPages = useMemo(() => {
    if (!surveyData) return [];

    const norm = (x) => ({
      id: x?.participant_id ?? x?.id,
      name: x?.name ?? x?.participant_name ?? "",
    });
    const allTM = (surveyData.participants?.team_members ?? []).map(norm);
    const allTL = (surveyData.participants?.team_leaders ?? []).map(norm);
    const allST = (surveyData.participants?.stakeholders ?? []).map(norm);

    const pages = [];
    let tmIdx = 0,
      tlIdx = 0,
      stIdx = 0;
    let offsets = { tm: 0, tl: 0, st: 0 };

    while (tmIdx < allTM.length || tlIdx < allTL.length || stIdx < allST.length) {
      let remaining = QUESTIONS_MAX_ACROSS;

      const takeTL = Math.min(remaining, Math.max(0, allTL.length - tlIdx));
      remaining -= takeTL;
      const takeST = Math.min(remaining, Math.max(0, allST.length - stIdx));
      remaining -= takeST;
      const takeTM = Math.min(remaining, Math.max(0, allTM.length - tmIdx));

      const page = {
        overrideVisible: {
          tl: allTL.slice(tlIdx, tlIdx + takeTL),
          st: allST.slice(stIdx, stIdx + takeST),
          tm: allTM.slice(tmIdx, tmIdx + takeTM),
        },
        labelOffsets: { ...offsets },
      };

      tlIdx += takeTL;
      offsets.tl += takeTL;
      stIdx += takeST;
      offsets.st += takeST;
      tmIdx += takeTM;
      offsets.tm += takeTM;

      pages.push(page);
    }

    if (pages.length === 0) {
      pages.push({
        overrideVisible: { tl: [], st: [], tm: [] },
        labelOffsets: { tm: 0, tl: 0, st: 0 },
      });
    }

    const pillarCount = surveyData?.pillars?.length ?? 0;
    const perPillar = Array.from({ length: pillarCount }, (_, pillarIdx) =>
      pages.map((p, i) => ({
        id: `questions-pdf-block-${pillarIdx + 1}-page-${i + 1}`,
        pillarIdx,
        overrideVisible: p.overrideVisible,
        labelOffsets: p.labelOffsets,
      }))
    );

    return perPillar;
  }, [surveyData]);

  // Generate PDF once the cover is shown
  useEffect(() => {
    if (!generatingReport) return;

    let cancelled = false;

    (async () => {
      await waitForLayout();

      const elem = document.getElementById("summary-pdf-block");
      if (!elem || cancelled) return;

      try {
        const canvas = await captureElementAsCanvas(elem);
        if (cancelled) return;
        finaliseReport(canvas.toDataURL("image/png"));
      } catch {
        setGeneratingReport(false);
        setShowReportCover(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [generatingReport, hasStakeholders, questionPages, showPillarTables]);

  return (
    <>
      <div className={`survey-overview-page-background-container${generatingReport ? " pdf-capture-active" : ""}`}>
        {surveyData ? (
          <>
            <div
              className="survey-overview-page-container"
              style={{ display: generatingReport ? "none" : "block" }}
            >
              {showSummary && !generatingReport ? (
                <SurveySummary
                  surveyData={surveyData}
                  showTop5s={updateShowTop5s}
                  hasStakeholders={hasStakeholders}
                  showPillars={updateShowPillars}
                  comparisonSurveys={comparisonSurveys}
                  surveyId={params.surveyId}
                  demo={!!searchParams.get("demo")}
                  generatingReport={false}
                  generateReport={OpenConfigurator}
                  setQuestionsMaps={setQuestionsMaps}
                />
              ) : null}

              {showTop5s && !generatingReport ? (
                <SummaryTop5s
                  surveyData={surveyData}
                  cardType={cardId}
                  back={updateShowSummary}
                  switchMenu={switchCard}
                  seeQuestions={(id) => updateShowQuestions(id, "top5s")}
                  surveyID={params.surveyId}
                  generatingReport={false}
                  demo={!!searchParams.get("demo")}
                  subscription={subscription}
                />
              ) : null}

              {showPillars && !generatingReport ? (
                <SummaryPillars
                  surveyData={surveyData}
                  cardType={cardId}
                  back={updateShowSummary}
                  switchMenu={switchCard}
                  seeQuestions={(id) => updateShowQuestions(id, "chart")}
                  surveyID={params.surveyId}
                  generatingReport={false}
                  demo={!!searchParams.get("demo")}
                  subscription={subscription}
                />
              ) : null}

              {showQuestions && !generatingReport ? (
                <SummaryQuestions
                  surveyData={surveyData}
                  cardType={cardId}
                  origin={questionsOrigin}
                  questionsData={questionsData}
                  teamVariance={getTeamVariance()[cardId] || []}
                  teamMean={getTeamMean()[cardId] || []}
                  back={backFromQuestions}
                  generatingReport={false}
                  demo={!!searchParams.get("demo")}
                  subscription={subscription}
                />
              ) : null}
            </div>

            {/* -------- PDF Render Container -------- */}
            <div
              className={`summary-report-pdf-render-container-block${generatingReport ? " pdf-capture-container" : ""}`}
              style={{ display: generatingReport ? "block" : "none" }}
            >
              <div className="summary-report-pdf-render-container">
                <SurveySummary
                  surveyData={surveyData}
                  showTop5s={() => {}}
                  showPillars={() => {}}
                  comparisonSurveys={comparisonSurveys}
                  surveyId={params.surveyId}
                  demo={!!searchParams.get("demo")}
                  generatingReport={generatingReport}
                  hideDetails={!showDetailCard}
                  hideChart={!showGraphicalChart}
                  hideCollective={!showCollectiveScores}
                  hasStakeholders={hasStakeholders}
                />
              </div>

              <div className="summary-report-pdf-render-container" id="top5s-pdf-block-1">
                <SummaryTop5s
                  surveyData={surveyData}
                  cardType={0}
                  back={() => {}}
                  switchMenu={() => {}}
                  surveyID={params.surveyId}
                  generatingReport={generatingReport}
                  demo={!!searchParams.get("demo")}
                  coachingSpacer={480}
                  hideGraphical={!showGraphicalData}
                  hideTabular={!showTabularData}
                  hideProgramme={!showCoachingProgramme}
                  hideExercise={!showCoachingExercises}
                  subscription={subscription}
                />
              </div>
              <div className="summary-report-pdf-render-container" id="top5s-pdf-block-2">
                <SummaryTop5s
                  surveyData={surveyData}
                  cardType={1}
                  back={() => {}}
                  switchMenu={() => {}}
                  surveyID={params.surveyId}
                  generatingReport={generatingReport}
                  demo={!!searchParams.get("demo")}
                  coachingSpacer={480}
                  hideGraphical={!showGraphicalData}
                  hideTabular={!showTabularData}
                  hideProgramme={!showCoachingProgramme}
                  hideExercise={!showCoachingExercises}
                  subscription={subscription}
                />
              </div>
              <div className="summary-report-pdf-render-container" id="top5s-pdf-block-3">
                <SummaryTop5s
                  surveyData={surveyData}
                  cardType={2}
                  back={() => {}}
                  switchMenu={() => {}}
                  surveyID={params.surveyId}
                  generatingReport={generatingReport}
                  demo={!!searchParams.get("demo")}
                  coachingSpacer={480}
                  hideGraphical={!showGraphicalData}
                  hideTabular={!showTabularData}
                  hideProgramme={!showCoachingProgramme}
                  hideExercise={!showCoachingExercises}
                  subscription={subscription}
                />
              </div>
              <div className="summary-report-pdf-render-container" id="top5s-pdf-block-4">
                <SummaryTop5s
                  surveyData={surveyData}
                  cardType={3}
                  back={() => {}}
                  switchMenu={() => {}}
                  surveyID={params.surveyId}
                  generatingReport={generatingReport}
                  demo={!!searchParams.get("demo")}
                  coachingSpacer={480}
                  hideGraphical={!showGraphicalData}
                  hideTabular={!showTabularData}
                  hideProgramme={!showCoachingProgramme}
                  hideExercise={!showCoachingExercises}
                  subscription={subscription}
                />
              </div>

              {/* Dynamic Question Pages */}
              {questionPages.flat().map(({ id, pillarIdx, overrideVisible, labelOffsets }) => (
                <div className="summary-report-pdf-render-container" id={id} key={id}>
                  <SummaryQuestions
                    surveyData={surveyData}
                    cardType={pillarIdx}
                    overrideVisible={overrideVisible}
                    forceExpanded={true}
                    maxVisible={QUESTIONS_MAX_ACROSS}
                    generatingReport={generatingReport}
                    teamVariance={teamVarianceByPillar[pillarIdx] || []}
                    teamMean={teamMeanByPillar[pillarIdx] || []}
                    back={() => {}}
                    labelOffsets={labelOffsets}
                    origin="chart"
                    subscription={subscription}
                  />
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>

      {/* Cover + dialogs */}
      <div
        className="report-generation-cover bold"
        style={{ display: generatingReport ? "flex" : "none" }}
      >
        <div className="lds-ring">
          <div></div><div></div><div></div><div></div>
        </div>
        Please wait, your report is being generated
      </div>

      {showConfigurator ? (
        <OverviewReportConfigurator
          generate={HandleConfiguratorGenerate}
          close={CloseConfigurator}
          saveConfig={setReportConfig}
          subscription={subscription}
          showDetailCard={showDetailCard}
          setShowDetailCard={setShowDetailCard}
          showGraphicalChart={showGraphicalChart}
          setShowGraphicalChart={setShowGraphicalChart}
          showCollectiveScores={showCollectiveScores}
          setShowCollectiveScores={setShowCollectiveScores}
          showGraphicalData={showGraphicalData}
          setShowGraphicalData={setShowGraphicalData}
          showTabularData={showTabularData}
          setShowTabularData={setShowTabularData}
          showCoachingProgramme={showCoachingProgramme}
          setShowCoachingProgramme={setShowCoachingProgramme}
          showCoachingExercises={showCoachingExercises}
          setShowCoachingExercises={setShowCoachingExercises}
          showPillarTables={showPillarTables}
          setShowPillarTables={setShowPillarTables}
        />
      ) : null}

      {showReportError ? (
        <>
          <div className="page-mask" onClick={() => setShowReportError(false)} />
          <div className="report-error-popup">
            <div className="report-error-title bold">Something Went Wrong</div>
            <div className="report-error-description regular">
              Failed to generate report. Please try again later.
            </div>
            <div className="report-error-button bold" onClick={() => setShowReportError(false)}>
              OK
            </div>
          </div>
        </>
      ) : null}

      {showReportGenerated ? (
        <>
          <div className="page-mask" onClick={() => setShowReportGenerated(false)} />
          <div className="report-error-popup">
            <div className="report-error-title bold">Report Generated</div>
            <div className="report-error-description regular">
              Your report has been generated and has been added to the reports table.
              Click Reports in the menu to see this table.
            </div>
            <div className="report-error-button bold" onClick={() => setShowReportGenerated(false)}>
              OK
            </div>
          </div>
        </>
      ) : null}
    </>
  );
};

export default SurveyOverview;
